import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import {EncryptedData, DecryptionError, ERROR_CODES} from '../../utils';
import {DecryptionManagerSingleton} from '../../utils/decryptionManager';

// eslint-disable-next-line
type ResponseBody = Record<string, any> | null | string;
type CustomFetchResponse = {
  body: ResponseBody;
  response: Response;
};

// eslint-disable-next-line
const customFetch = async (url: string | Request, options: any) => {
  const decryptionManager = DecryptionManagerSingleton.getDecryptionManager();
  let decryptionKey: CryptoKey | null = null;

  if (decryptionManager?.isEnabled() && options?.requireEncryption) {
    decryptionKey = await decryptionManager.getDecryptionKey();
    const encryptionHeaders = decryptionManager.getEncryptionHeaders();

    if (Object.keys(encryptionHeaders).length > 0) {
      if (!options.headers) {
        options.headers = new Headers(encryptionHeaders);
      } else {
        Object.entries(encryptionHeaders).forEach(([key, value]) => {
          options.headers.set(key, value);
        });
      }
    }
  }

  return new Promise<CustomFetchResponse>((resolve, reject) => {
    return fetch(url, options)
      .then(async (response: Response) => {
        let body = await getResponseBody(response);

        if (
          decryptionManager?.isEnabled() &&
          decryptionManager.isResponseEncrypted(response) &&
          decryptionKey
        ) {
          try {
            body = await decryptionManager.decryptResponse(
              body as EncryptedData,
              decryptionKey,
            );
          } catch (error) {
            console.error('Failed to decrypt response:', error);
            Sentry.captureMessage('Failed to decrypt response');
            throw new DecryptionError(
              i18next.t('something_went_wrong'),
              ERROR_CODES.decryptionError,
            );
          }
        }

        return checkStatus({response, body});
      })
      .then(resolve)
      .catch(reject);
  });
};

const getResponseBody = (response: Response): Promise<ResponseBody> => {
  const contentType = response.headers.get('content-type');
  return contentType && contentType.indexOf('json') >= 0
    ? response.clone().text().then(tryParseJSON)
    : response.clone().text();
};

const tryParseJSON = (json: string): Record<string, unknown> | null => {
  if (!json) {
    return null;
  }

  try {
    return JSON.parse(json);
  } catch {
    throw new Error(`Failed to parse unexpected JSON response: ${json}`);
  }
};

class ResponseError extends Error {
  status: number;
  response: Response;
  body: ResponseBody;

  constructor(status: number, response: Response, body: ResponseBody) {
    super();
    this.name = 'ResponseError';
    this.status = status;
    this.response = response;
    this.body = body;
  }
}

const checkStatus = ({response, body}: {response: Response; body: ResponseBody}) => {
  if (response.ok) {
    return {response, body};
  } else {
    throw new ResponseError(response.status, response, body);
  }
};

export default customFetch;
